/**
 * Server-side persistence for endoscopy still-image labelling.
 *
 * Mirrors the role the report/label API plays for the video side: what
 * a reader records on a snapshot is no longer trapped in the browser's
 * localStorage — it round-trips through the cluster keyed by SOP
 * Instance UID, so it survives a reload, follows the reader to another
 * workstation, and is available to the ML-export path.
 *
 * The unit of persistence is the per-image DOC (one per instance per
 * user):
 *   · procedure    — selects the controlled vocabulary (colonoscopy/egd)
 *   · image_label  — the whole-frame label (landmark + lesion/Normal)
 *   · calibration  — the mm-per-pixel scale the reader set against a
 *                    known-size in-frame reference (endoscopy frames
 *                    carry no DICOM pixel-spacing — see lesionTaxonomy.md)
 *   · annotations  — the geometry array (freehand/arrow/distance/
 *                    polygon/bbox/text), each tagged with a lesion class
 *
 * Decoupled from the report save flow on purpose: image labels live in
 * their own `endo_image_annotations` row, so persisting a scribble
 * never risks the finalized report's anatomy spans / lesion ROIs.
 *
 * localStorage is kept as an offline cache + fallback: if the network
 * is down the reader still sees and edits their work, and the next
 * successful PUT reconciles it server-side.
 */
import { auth } from "@viewer/auth/store";

const LS_PREFIX = "vpd.endo.still.doc.";
const LS_VER    = 2;

export interface ImageLabel {
  /** Anatomical landmark — taxonomy id (preset) or "" with a custom name. */
  landmark_id?:   string;
  landmark_name?: string;
  /** Lesion — taxonomy id (preset) or "" with a custom name; both empty
   *  + normal=true means the reader marked the frame Normal. */
  lesion_id?:     string;
  lesion_name?:   string;
  /** Grade within the lesion's scheme (Paris/NICE/Forrest/…), if chosen. */
  grade_id?:      string;
  /** Reader explicitly marked "Normal — no lesion". */
  normal?:        boolean;
}

export interface Calibration {
  /** Millimetres per image pixel, from the reader's reference line. */
  mmPerPx: number;
  /** The real length (mm) the reader entered for the reference. */
  ref_mm:  number;
}

export interface ImageDoc {
  procedure:   "colonoscopy" | "egd";
  image_label: ImageLabel;
  calibration: Calibration | null;
  annotations: any[];
}

export function emptyDoc(): ImageDoc {
  // Default procedure is Upper GI (EGD). A fresh, never-labelled image
  // hydrates from this, so this is what the procedure toggle shows until
  // the reader picks otherwise.
  return { procedure: "egd", image_label: {}, calibration: null, annotations: [] };
}

function authHeaders(): Record<string, string> {
  const a = auth();
  return a?.accessToken ? { Authorization: `Bearer ${a.accessToken}` } : {};
}

function normalize(raw: Partial<ImageDoc> | null | undefined): ImageDoc {
  const d = emptyDoc();
  if (!raw) return d;
  if (raw.procedure === "egd" || raw.procedure === "colonoscopy") d.procedure = raw.procedure;
  if (raw.image_label && typeof raw.image_label === "object") d.image_label = raw.image_label;
  if (raw.calibration && typeof (raw.calibration as any).mmPerPx === "number") d.calibration = raw.calibration as Calibration;
  if (Array.isArray(raw.annotations)) d.annotations = raw.annotations;
  return d;
}

function lsRead(uid: string): ImageDoc | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + uid);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j || j.v !== LS_VER || !j.doc) return null;
    return normalize(j.doc);
  } catch { return null; }
}
function lsWrite(uid: string, doc: ImageDoc): void {
  try {
    localStorage.setItem(LS_PREFIX + uid, JSON.stringify({ v: LS_VER, doc, savedAt: Date.now() }));
  } catch { /* quota — the in-memory copy is still authoritative */ }
}

/**
 * Load the doc for one instance. Tries the server; on any failure
 * falls back to the localStorage cache so the reader is never staring
 * at a blank overlay because of a transient blip.
 */
export async function loadImageDoc(studyUid: string, instanceUid: string): Promise<ImageDoc> {
  try {
    const r = await fetch(
      `/viewer-api/endo/image-annotations/${encodeURIComponent(instanceUid)}?study=${encodeURIComponent(studyUid)}`,
      { headers: { Accept: "application/json", ...authHeaders() } },
    );
    if (r.ok) {
      const j = await r.json() as Partial<ImageDoc>;
      const doc = normalize(j);
      lsWrite(instanceUid, doc);   // refresh the offline cache
      return doc;
    }
  } catch { /* fall through to cache */ }
  return lsRead(instanceUid) ?? emptyDoc();
}

/** One instance's stored doc, as returned by the study-level read. */
export interface StudyImageDoc extends ImageDoc { instance_uid: string }

/**
 * Load every instance's annotation doc for a whole study (current
 * user) in one request — backs the "Automate drafting" aggregation.
 * Returns only instances that have a stored doc.
 */
export async function loadStudyImageDocs(studyUid: string): Promise<StudyImageDoc[]> {
  try {
    const r = await fetch(
      `/viewer-api/endo/image-annotations?study=${encodeURIComponent(studyUid)}`,
      { headers: { Accept: "application/json", ...authHeaders() } },
    );
    if (r.ok) {
      const j = await r.json() as { docs?: any[] };
      if (Array.isArray(j.docs)) {
        return j.docs.map((d) => ({ ...normalize(d), instance_uid: String(d.instance_uid) }));
      }
    }
  } catch { /* offline — caller can fall back to per-instance caches */ }
  return [];
}

/**
 * Persist the doc for one instance. Writes the local cache and PUTs to
 * the server. Fire-and-forget from the caller's perspective; a failure
 * keeps the local copy until the next save.
 */
export async function saveImageDoc(studyUid: string, instanceUid: string, doc: ImageDoc): Promise<void> {
  lsWrite(instanceUid, doc);
  try {
    await fetch(
      `/viewer-api/endo/image-annotations/${encodeURIComponent(instanceUid)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json", Accept: "application/json", ...authHeaders() },
        body: JSON.stringify({
          study_uid:   studyUid,
          procedure:   doc.procedure,
          image_label: doc.image_label,
          calibration: doc.calibration,
          annotations: doc.annotations,
        }),
      },
    );
  } catch { /* offline — localStorage holds it until the next save */ }
}
